import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

export interface JobReward {
  CodeName128: string;
  Quanty: number;
  icon_url: string;
  item_name: string;
}

export interface JobData {
  jobType: number;
  jobTypeName: string;
  jobLevel: number;
  totalRequired: number;
  dailyProgress: number;
  progressBreakdown: Record<number, number>;
  progressPercent: number;
  isCompleted: boolean;
  claimedToday: boolean;
  reward: JobReward | null;
  icon_url: string;
}

export interface JobLogRow {
  OriginCity: number;
  TargetCity: number;
  RemaindGold: number;
  DateStart: string;
  DateEnd: string;
  JobType: number;
  origin_city_name: string;
  target_city_name: string;
  is_special_route: boolean;
  special_route_text?: 'protected_cargo' | 'stolen_cargo';
  delivery_time_minutes: number;
  delivery_time_formatted: string;
}

export type JobSystemErrorKey =
  | 'login_required'
  | 'no_job_info'
  | 'hwid_error'
  | 'level_error'
  | 'no_configuration';

export interface JobSystemState {
  jobData: JobData | null;
  logs: JobLogRow[];
  error: JobSystemErrorKey | null;
}

export function jobSystemQueryKey(charId: number) {
  return ['job-system', charId] as const;
}

export async function fetchJobSystem(): Promise<JobSystemState> {
  const { data } = await api.get<JobSystemState & { status: string }>('/api/v1/job-system');
  return data;
}

export function useJobSystem(charId: number) {
  return useQuery({
    queryKey: jobSystemQueryKey(charId),
    queryFn: fetchJobSystem,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useClaimJobReward(charId: number) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: jobSystemQueryKey(charId) });

  return useMutation({
    mutationFn: async (jobType: number) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/job-system/claim',
        { job_type: jobType },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => invalidate(), 1200);
      }
    },
  });
}

export function prefetchJobSystem(queryClient: QueryClient, charId: number): void {
  if (!charId) return;
  void queryClient.prefetchQuery({
    queryKey: jobSystemQueryKey(charId),
    queryFn: fetchJobSystem,
    staleTime: 30_000,
  });
}
